import React, { useState, useEffect } from 'react'
import { Row, Form, Col, FormSelect, Button, Table, Modal, InputGroup } from "react-bootstrap";
import { ToastContainer } from 'react-toastify';
import axiosInstance from '../../utils/axios';
import { formatDisplayDate } from '../../utils/formatter';
import { showToast } from '../reusables/CommonFunction';
import CustomPagination from '../reusables/CustomPagination';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';

const DemoRequestList = () => {
    const clinicType = {
        'AL': 'Allopathy',
        'AY': 'Ayurvedic',
        'HM': 'Homeopathy'
    }
    const productPurchased = {
        '1': 'Enquiry',                 // User shows initial interest in the product.
        '2': 'Lead',                    // User is identified as a potential customer.
        '3': 'Follow Up',               // Sales team follows up with the lead.
        '4': 'Meeting Scheduled',       // A product demo/meeting is arranged.
        '5': 'Trial Provided',          // User is given a trial/demo of the product.
        '6': 'Quotation Sent',          // Price details are shared with the user.
        '7': 'Negotiation',             // Discussion on pricing, features, and terms.
        '8': 'Waiting for Decision',    // User is deciding after getting the quote.
        '9': 'Booked',                  // User has confirmed interest but not yet purchased.
        '10': 'Purchased',              // The user has completed the purchase. ✅
        '11': 'Rejected',               // The user has **decided not to buy**. ❌
        '12': 'Cancelled',              // User initially booked but later canceled.
        '13': 'On Hold',                // Temporarily paused due to internal/external reasons.
    };
    const [loading, setLoading] = useState(false);
    const [clinicName, setClinicName] = useState("");
    const [demoRequestDetails, setDemoRequestDetails] = useState([]);
    const [isEditModalOpen, setEditModalOpen] = useState({});
    const [isSubmitting, setIsubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
    const [demoRequestState, setDemoRequestState] = useState({
        slug: "",
        demo_id: "",
        first_name: "",
        last_name: "",
        email_id: "",
        mobile_number: "",
        clinic_name: "",
        clinic_type: "",
        additional_information: "",
        admin_remark: "",
        product_purchased: ""
    });

    const getDemoRequestDetails = (pageNumber) => {
        setLoading(true);
        axiosInstance
            .get('/demorequests/', {
                params: {
                    page: pageNumber,
                    q: clinicName,
                },
            })
            .then((response) => {
                if (response.status === 200) {
                    setDemoRequestDetails(response.data.data);
                    setTotalCount(response.data.count);
                }
            })
            .catch(() => {
                showToast("error", "Failed to fetch demo request details");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onPressSearchDemoRequest = () => {
        setCurrentPage(1); // Reset to the first page
        getDemoRequestDetails(1);
    };

    const onPressEditDemoRequest = (oEvent, pDemoRequest) => {
        oEvent.preventDefault();
        setEditModalOpen({ ['isModalOpen_' + pDemoRequest.demo_id]: true })
        setDemoRequestState({
            slug: pDemoRequest.slug,
            demo_id: pDemoRequest.demo_id,
            first_name: pDemoRequest.first_name,
            last_name: pDemoRequest.last_name,
            email_id: pDemoRequest.email_id,
            mobile_number: pDemoRequest.mobile_number,
            clinic_name: pDemoRequest.clinic_name,
            clinic_type: pDemoRequest.clinic_type,
            additional_information: pDemoRequest.additional_information,
            admin_remark: pDemoRequest.admin_remark,
            product_purchased: pDemoRequest.product_purchased
        })
    }

    const onPressSubmitEditDemoRequest = (oEvent) => {
        oEvent.preventDefault();
        setIsubmitting(true)
        setLoading(true);

        axiosInstance
            .put(`/demorequests/${demoRequestState.slug}/`, demoRequestState)
            .then((response) => {
                if (response.status === 200) {
                    showToast("success", response.data.message, { onClose: () => setEditModalOpen({ ['isModalOpen_' + demoRequestState.demo_id]: false }) });
                    setCurrentPage(1); // Reset to the first page
                    getDemoRequestDetails(1);

                    setDemoRequestState({
                        slug: "",
                        demo_id: "",
                        first_name: "",
                        last_name: "",
                        email_id: "",
                        mobile_number: "",
                        clinic_name: "",
                        clinic_type: "",
                        additional_information: "",
                        admin_remark: "",
                        product_purchased: ""
                    })
                }
            })
            .catch((error) => {
                const errorData = error.response?.data;
                if (errorData) {
                    Object.keys(errorData).forEach((key, index) => {
                        let sError = Array.isArray(errorData[key]) ? errorData[key][0] : errorData[key];
                        if (sError.includes("demoRequest")) {
                            sError = sError.replace("demoRequest", "Demo Request");
                        }
                        showToast("error", sError, { toastId: index });
                    });
                } else {
                    showToast("error", "An unexpected error occurred. Please try again.");
                }
            })
            .finally(() => {
                setIsubmitting(false);
                setLoading(false);
            });
    }

    useEffect(() => {
        getDemoRequestDetails(currentPage);
    }, [currentPage]);

    return (
        <Row className="ml-3 mr-3 mb-3">
            <div className='col-md-12'>
                <InputGroup className="mb-3 col-md-4 pl-0" >
                    <Form.Control
                        type="text"
                        name="clinicName"
                        onChange={(event) => setClinicName(event.target.value)}
                        placeholder="Enter Clinic Name..."
                        value={clinicName}
                    />
                    <Button type="submit" variant="outline-secondary" onClick={onPressSearchDemoRequest}>
                        Search
                    </Button>
                </InputGroup>

                {/* If the count is 0 then hide the table */}
                {demoRequestDetails.length > 0 ?
                    <>
                        <Table bordered hover responsive>
                            <thead light="true">
                                <tr>
                                    <th style={{ width: "2rem" }}>#</th>
                                    <th style={{ width: "20rem" }}>Clinic Name</th>
                                    <th style={{ width: "6rem" }}>Clinic Type</th>
                                    <th style={{ width: "10rem" }}>Requestor Name</th>
                                    <th style={{ width: "8rem" }}>Mobile Number</th>
                                    <th style={{ width: "6rem" }}>Requested On</th>
                                    <th style={{ width: "10rem" }}>Product Purchased</th>
                                    <th style={{ width: "3rem" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {demoRequestDetails.map((demo, index) => {
                                    return [
                                        <tr key={demo.demo_id}>
                                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                            <td>{demo.clinic_name}</td>
                                            <td>{clinicType[demo.clinic_type]}</td>
                                            <td>{demo.first_name} {demo.last_name}</td>
                                            <td>{demo.mobile_number}</td>
                                            <td>{formatDisplayDate(demo.created_on)}</td>
                                            <td>{demo.product_purchased}. {productPurchased[demo.product_purchased]}</td>
                                            <td>
                                                <Button type='button' variant='info' size='sm' onClick={oEvent => onPressEditDemoRequest(oEvent, demo)} >Edit</Button>
                                                <Modal size="lg" backdrop="static" keyboard={false} centered
                                                    show={isEditModalOpen['isModalOpen_' + demo.demo_id]}
                                                    onHide={() => setEditModalOpen({ ['isModalOpen_' + demo.demo_id]: false })} >
                                                    <Modal.Header closeButton>
                                                        <Modal.Title>Edit Demo Requestor Details</Modal.Title>
                                                    </Modal.Header>
                                                    <Modal.Body>
                                                        <Form autoComplete="off" noValidate>
                                                            <Row className="mb-3">
                                                                <Form.Group as={Col} controlId="formFirstName">
                                                                    <Form.Label>First Name</Form.Label>
                                                                    <Form.Control disabled
                                                                        type="text"
                                                                        name="first_name"
                                                                        value={demoRequestState.first_name || ""}
                                                                    />
                                                                </Form.Group>

                                                                <Form.Group as={Col} controlId="formLastName">
                                                                    <Form.Label>Last Name</Form.Label>
                                                                    <Form.Control disabled
                                                                        type="text"
                                                                        name="last_name"
                                                                        value={demoRequestState.last_name || ""}
                                                                    />
                                                                </Form.Group>
                                                            </Row>

                                                            <Row className="mb-3">
                                                                <Form.Group as={Col} controlId="formEmailId">
                                                                    <Form.Label>Email Address</Form.Label>
                                                                    <Form.Control disabled
                                                                        type="text"
                                                                        name="email_id"
                                                                        value={demoRequestState.email_id || ""}
                                                                    />
                                                                </Form.Group>

                                                                <Form.Group as={Col} controlId="formMobileNumber">
                                                                    <Form.Label>Mobile Number</Form.Label>
                                                                    <Form.Control disabled
                                                                        type="text"
                                                                        name="mobile_number"
                                                                        value={demoRequestState.mobile_number || ""}
                                                                    />
                                                                </Form.Group>
                                                            </Row>

                                                            <Form.Group className="mb-3" controlId="formClinicName">
                                                                <Form.Label>Clinic Name</Form.Label>
                                                                <Form.Control disabled
                                                                    type="text"
                                                                    name="clinic_name"
                                                                    value={demoRequestState.clinic_name || ""}
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="formClinicType">
                                                                <Form.Label>Clinic Type</Form.Label>
                                                                <Form.Control disabled
                                                                    type="text"
                                                                    name="clinic_type"
                                                                    value={clinicType[demoRequestState.clinic_type] || ""}
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="formAdditionalInfo">
                                                                <Form.Label>Additional Information</Form.Label>
                                                                <Form.Control as="textarea" rows={3} disabled
                                                                    name="additional_information"
                                                                    value={demoRequestState.additional_information || ""}
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="formRemark">
                                                                <Form.Label className="required">Admin Remark</Form.Label>
                                                                <Form.Control as="textarea" rows={3}
                                                                    name="admin_remark"
                                                                    placeholder="Admin Remark"
                                                                    value={demoRequestState.admin_remark || ""}
                                                                    onChange={(oEvent) => setDemoRequestState({ ...demoRequestState, [oEvent.target.name]: oEvent.target.value })}
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="formProductPurchased">
                                                                <Form.Label>Product Purchased</Form.Label>
                                                                <Form.Control
                                                                    as={FormSelect}
                                                                    name="product_purchased"
                                                                    value={demoRequestState.product_purchased || ""}
                                                                    onChange={(oEvent) => {
                                                                        setDemoRequestState({ ...demoRequestState, product_purchased: oEvent.target.value });
                                                                    }}>
                                                                    {Object.entries(productPurchased).map(([key, label]) => (
                                                                        <option key={key} value={key}>
                                                                            {key}. {label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Control>
                                                            </Form.Group>
                                                        </Form>
                                                    </Modal.Body>
                                                    <Modal.Footer>
                                                        <Button type="submit" variant="primary" className="form-control" onClick={onPressSubmitEditDemoRequest}>
                                                            {isSubmitting ? 'Submitting...' : 'Submit'}
                                                        </Button>
                                                    </Modal.Footer>
                                                </Modal>
                                            </td>
                                        </tr>
                                    ]
                                })}
                            </tbody>
                        </Table>

                        <CustomPagination
                            totalCount={totalCount}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage} />
                    </>
                    : <> </>}
                <ToastContainer />
                <BusyLoaderOverlay show={loading} />
            </div>
        </Row>
    )
}
export default DemoRequestList